// Lead channel (#1544): where a plan wizard lead came from, derived once at
// submit time from the stored first-touch attribution (UTMs, click ids, the
// Meta ad id) and the first landing (referrer host, landing path). Stored on
// `events.lead_channel` as sent, never recomputed, so a report reads it as is.

export const LEAD_CHANNELS = [
  "meta_ads",
  "google_ads",
  "organic_search",
  "ai_assistant",
  "gbp",
  "social_organic",
  "referral",
  "direct",
  "unknown",
] as const;
export type LeadChannel = (typeof LEAD_CHANNELS)[number];

export interface LeadChannelInput {
  utm_source?: string | null;
  utm_medium?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  meta_ad_id?: string | null;
  referrer_host?: string | null;
  landing_path?: string | null;
}

/** Our own sites. A referrer from one means the real source was lost before an instrumented page. */
export const OWN_DOMAINS = ["tristanoscatering.ca"] as const;

/** Checked before search, so gemini.google.com is not read as Google. */
export const AI_ASSISTANT_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "perplexity.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "claude.ai",
] as const;
export const AI_ASSISTANT_NAMES = ["chatgpt", "perplexity", "gemini", "copilot", "claude"] as const;

export const SEARCH_HOSTS = [
  "com.google.android.googlequicksearchbox", // the Google app's referrer host
  "bing.com",
  "duckduckgo.com",
  "yahoo.com",
] as const;
export const SEARCH_NAMES = ["bing", "duckduckgo", "yahoo"] as const;
// google.com, google.ca, google.co.uk, google.com.au, …
const GOOGLE_SEARCH_RE = /(^|\.)google\.(com?\.)?[a-z]{2,3}$/;

export const SOCIAL_HOSTS = [
  "facebook.com",
  "fb.com",
  "instagram.com",
  "threads.net",
  "linkedin.com",
  "lnkd.in",
  "x.com",
  "t.co",
  "twitter.com",
  "tiktok.com",
  "pinterest.com",
  "pinterest.ca",
  "youtube.com",
  "reddit.com",
  "com.facebook.katana",
  "com.instagram.android",
  "com.linkedin.android",
] as const;
export const SOCIAL_NAMES = [
  "facebook",
  "instagram",
  "threads",
  "linkedin",
  "x",
  "twitter",
  "tiktok",
  "pinterest",
  "youtube",
  "reddit",
] as const;

// Meta's placement macro values and the app's own creative tag.
const META_SOURCES: ReadonlySet<string> = new Set(["meta", "fb", "ig", "an", "msg"]);
const PAID_MEDIUMS: ReadonlySet<string> = new Set([
  "cpc",
  "ppc",
  "paid",
  "paid_social",
  "paidsocial",
  "paid_search",
  "paidsearch",
  "cpm",
  "display",
]);

function clean(value: string | null | undefined): string | null {
  const v = value?.trim().toLowerCase();
  return v ? v : null;
}

function matchesDomain(host: string, domains: readonly string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
}

/** Channel for a referrer host, or a bare source name such as `chatgpt` or `bing`. */
export function leadChannelFromHost(rawHost: string): LeadChannel {
  const host = clean(rawHost)?.replace(/^www\./, "");
  if (!host) return "unknown";
  if (matchesDomain(host, OWN_DOMAINS)) return "unknown";
  if (matchesDomain(host, AI_ASSISTANT_HOSTS) || (AI_ASSISTANT_NAMES as readonly string[]).includes(host)) {
    return "ai_assistant";
  }
  if (
    GOOGLE_SEARCH_RE.test(host) ||
    matchesDomain(host, SEARCH_HOSTS) ||
    (SEARCH_NAMES as readonly string[]).includes(host)
  ) {
    return "organic_search";
  }
  if (matchesDomain(host, SOCIAL_HOSTS) || (SOCIAL_NAMES as readonly string[]).includes(host)) {
    return "social_organic";
  }
  return "referral";
}

/** First match wins: click ids, then UTMs, then a bare fbclid, then the referrer, then the landing. */
export function leadChannel(input: LeadChannelInput): LeadChannel {
  const source = clean(input.utm_source);
  // `paid-social` and `Paid_Social` are both `paid_social` (#1576).
  const medium = clean(input.utm_medium)?.replace(/-/g, "_") ?? null;
  const paid = medium !== null && PAID_MEDIUMS.has(medium);

  if (clean(input.gclid)) return "google_ads";
  // Every creative the app builds carries ad_id (#1283).
  if (clean(input.meta_ad_id)) return "meta_ads";
  // utm_source=website is the marketing CTA's own tag (#889), not an acquisition source.
  if (source && source !== "website") {
    if (source === "gbp" || source === "gmb") return "gbp";
    if (META_SOURCES.has(source)) return "meta_ads";
    if (source === "facebook" || source === "instagram") return paid ? "meta_ads" : "social_organic";
    if (source === "google" || source === "adwords") return paid ? "google_ads" : "organic_search";
    const fromName = leadChannelFromHost(source);
    if (fromName === "ai_assistant" || fromName === "organic_search" || fromName === "social_organic") {
      return fromName;
    }
    return "referral";
  }
  // Facebook and Instagram add fbclid to organic clicks too (the /ig bio links).
  if (clean(input.fbclid)) return "social_organic";
  const referrer = clean(input.referrer_host);
  if (referrer) return leadChannelFromHost(referrer);
  if (clean(input.landing_path)) return "direct";
  return "unknown";
}
